/**
 * Publishing keys (ADR-0017): let MSA Creator send documents into DocVault as
 * drafts or new versions. Platform admins only. A key is shown once.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, type PublishKeyRow } from "../lib/api";
import { useStore } from "../lib/store";
import { coName } from "../lib/brand";
import { fmt, fmtDT } from "../lib/format";
import { Loading, Tile } from "../components/bits";
import { PartTag } from "./settings/SettingsLayout";

const SCOPE_LABEL: Record<string, string> = { documents: "Documents", branding: "Branding" };

export function PublishKeysPage() {
  const { me, p, toast } = useStore();
  const [keys, setKeys] = useState<PublishKeyRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState("MSA Creator");
  const [cos, setCos] = useState<string[]>(p.myCodes.includes("ITA") ? ["ITA"] : p.myCodes.slice(0, 1));
  const [scopes, setScopes] = useState<string[]>(["documents"]);
  const [owner, setOwner] = useState(me.user.id);
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [shown, setShown] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.publishKeys().then((r) => setKeys(r.keys), (e: Error) => setErr(e.message));
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (!cos[0]) return;
    api.peopleIn(cos[0]).then((r) => setPeople(r.people.filter((x) => x.roles.some((role) => role !== "reader"))), () => undefined);
  }, [cos]);
  if (!me.user.is_platform_admin) return <Navigate to="/" replace />;
  if (err) return <div className="card empty view"><h3>Keys didn't load</h3>{err}</div>;
  if (!keys) return <Loading />;

  const toggle = (list: string[], v: string, set: (x: string[]) => void) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.createPublishKey({ name: name.trim(), companies: cos, scopes, owner_id: owner });
      setShown(r.key); await load();
    } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  const revoke = async (k: PublishKeyRow) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working at once. Documents it already sent stay.`)) return;
    try { await api.revokePublishKey(k.id); toast(`Revoked ${k.name}.`); await load(); } catch (e) { toast((e as Error).message); }
  };

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <PartTag part="vault" />
          <h1 className="title">Publishing <em>keys</em></h1>
          <div className="lede">Let MSA Creator send documents into DocVault. A key can create drafts, upload new versions and submit them for approval; a person still approves every document.</div>
        </div>
      </div>

      {shown && (
        <div className="card key-shown">
          <h3>Copy this key now</h3>
          <p>DocVault only stores a fingerprint of it, so it can't be shown again. Keep it in 1Password, and give it to MSA Creator as <span className="mono">DOCVAULT_KEY</span>.</p>
          <div className="key-box"><code>{shown}</code>
            <button className="btn btn-accent btn-sm" onClick={() => { void navigator.clipboard.writeText(shown).then(() => toast("Key copied.")); }}>Copy</button></div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShown(null)}>I've saved it</button>
        </div>
      )}

      <div className="card tbl-wrap">
        <table className="tbl">
          <thead><tr><th>Key</th><th>Companies</th><th className="hide-md">Can</th><th className="hide-md">Owner of what it sends</th><th>Last used</th><th><span className="sr">Actions</span></th></tr></thead>
          <tbody>
            {keys.length === 0 && <tr><td colSpan={6} className="cap">No keys yet.</td></tr>}
            {keys.map((k) => (
              <tr key={k.id} className={k.revoked_at ? "retired" : ""}>
                <td><b>{k.name}</b><div className="cap mono">dvp_…{k.key_hint}</div></td>
                <td><span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>{k.companies.map((c) => <Tile key={c} code={c} />)}</span></td>
                <td className="hide-md">{k.scopes.map((s) => SCOPE_LABEL[s] ?? s).join(", ")}</td>
                <td className="hide-md">{k.owner_name}</td>
                <td>{k.revoked_at ? <span className="pill draft">Revoked {fmt(k.revoked_at)}</span> : k.last_used_at ? fmtDT(k.last_used_at) : <span className="cap">Never</span>}</td>
                <td style={{ textAlign: "right" }}>{!k.revoked_at && <button className="btn btn-ghost btn-sm" onClick={() => void revoke(k)}>Revoke</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card add-code">
        <h3>New key</h3>
        <div className="add-code-row">
          <label className="grow"><span className="lbl">Name</span><input className="inp" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} /></label>
          <label><span className="lbl">Owner of what it sends</span>
            <select className="inp" value={owner} onChange={(e) => setOwner(e.target.value)}>
              {!people.some((x) => x.id === me.user.id) && <option value={me.user.id}>{me.user.display_name}</option>}
              {people.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select></label>
        </div>
        <div className="key-opts">
          <span className="lbl">Companies</span>
          {p.myCodes.map((c) => (
            <label key={c} className="chk"><input type="checkbox" checked={cos.includes(c)} onChange={() => toggle(cos, c, setCos)} />{coName(c)}</label>
          ))}
        </div>
        <div className="key-opts">
          <span className="lbl">It can</span>
          <label className="chk"><input type="checkbox" checked={scopes.includes("documents")} onChange={() => toggle(scopes, "documents", setScopes)} />Create drafts and new versions</label>
          <label className="chk"><input type="checkbox" checked={scopes.includes("branding")} onChange={() => toggle(scopes, "branding", setScopes)} />Update company color and logo</label>
        </div>
        <div className="row-btns"><button className="btn btn-accent" disabled={busy || !cos.length || !scopes.length || name.trim().length < 2} onClick={() => void create()}>Create key</button></div>
        <div className="help">Documents it creates belong to the owner you pick (reminders go to them). Company-wide documents can't be published with a key.</div>
      </div>
    </div>
  );
}
